import type {
  Guide,
  HeaderLink,
  HeaderLinkExternal,
  HeaderLinkGroup,
  HeaderLinkGuide,
  HeaderLinkReference,
  HeaderLinkSpacer,
  HeaderLinkVersionSelector,
  Reference,
} from '@scalar-org/entities/project'
import { nanoid } from '@scalar-org/entities/utility'

import type {
  ConfigHeader,
  ConfigHeaderExternalLink,
  ConfigHeaderGroup,
  ConfigHeaderGuide,
  ConfigHeaderReference,
} from '@/legacy/legacy'

function resolveHeaderReference({
  config,
  references,
}: {
  config: ConfigHeaderReference
  references: Map<string, Pick<Reference, 'uid' | 'title'>>
}): HeaderLinkReference {
  const reference = references.get(config.slug)

  if (!reference) {
    throw Error(
      `Reference header item with slug '${config.slug}' could not be found. Please double check your header items.`,
    )
  }

  return {
    uid: reference.uid,
    title: reference.title,
    type: 'reference',
    hasPermalink: false,
    hasManagedDoc: false,
    show: true,
    isStarred: false,
  }
}

function resolveHeaderGuide({
  config,
  guides,
}: {
  config: ConfigHeaderGuide
  guides: Map<string, Pick<Guide, 'uid' | 'title'>>
}): HeaderLinkGuide {
  const guide = guides.get(config.slug)

  if (!guide) {
    throw Error(
      `Guide header item with slug '${config.slug}' could not be found. Please double check your header items.`,
    )
  }

  return {
    uid: guide.uid,
    title: guide.title,
    type: 'guide',
    show: true,
    isStarred: false,
  }
}

function resolveHeaderLink(
  config: ConfigHeaderExternalLink,
): HeaderLinkExternal {
  return {
    uid: nanoid(),
    type: 'link',
    show: true,
    isStarred: false,
    title: config.title,
    link: config.link,
    isButton: config.isButton,
  }
}

function resolveHeaderSpacer(): HeaderLinkSpacer {
  return {
    uid: nanoid(),
    title: '',
    isStarred: false,
    type: 'spacer',
    show: true,
  }
}

function resolveHeaderVersionSelector(): HeaderLinkVersionSelector {
  return {
    uid: nanoid(),
    title: 'Version Selector',
    isStarred: false,
    type: 'version-selector',
    show: true,
  }
}

function resolveHeaderGroup({
  config,
  guides,
  references,
}: {
  config: ConfigHeaderGroup
  guides: Map<string, Pick<Guide, 'uid' | 'title'>>
  references: Map<string, Pick<Reference, 'uid' | 'title'>>
}): HeaderLinkGroup {
  return {
    uid: nanoid(),
    type: 'group',
    title: config.title,
    children: config.children.map((child) => {
      switch (child.type) {
        case 'guide':
          return resolveHeaderGuide({ guides, config: child })
        case 'reference':
          return resolveHeaderReference({ references, config: child })
        case 'link':
          return resolveHeaderLink(child)
      }
    }),
    isStarred: false,
    show: true,
  }
}

export function resolveHeader({
  references,
  guides,
  header,
}: {
  references: Map<string, Pick<Reference, 'uid' | 'title'>>
  guides: Map<string, Pick<Guide, 'uid' | 'title'>>
  header: ConfigHeader[]
}): HeaderLink[] {
  return header.map((config): HeaderLink => {
    switch (config.type) {
      case 'guide':
        return resolveHeaderGuide({ config, guides })
      case 'reference':
        return resolveHeaderReference({ config, references })
      case 'link':
        return resolveHeaderLink(config)
      case 'group':
        return resolveHeaderGroup({ config, guides, references })
      case 'version-selector':
        return resolveHeaderVersionSelector()
      case 'spacer':
        return resolveHeaderSpacer()
    }
  })
}
